// BXYZ:..:eliot@bosmanxyz.xyz:..:.www.bosmanxyz.xyz
// Interactive Form with GSAP + Lenis Integration

class BXYZInteractiveForm {
  constructor(formElement) {
    this.form = formElement;
    this.container = formElement.closest('.bxyz-interactive-form');
    this.steps = Array.from(formElement.querySelectorAll('.bxyz-form-step'));
    this.currentStep = 0;
    this.totalSteps = this.steps.length;
    
    // Settings from data attributes
    this.stepMode = this.container.dataset.stepMode === 'true';
    this.animationDuration = parseInt(this.container.dataset.animationDuration) / 1000 || 0.6;
    this.animationEasing = this.container.dataset.animationEasing || 'power2.out';
    
    // Form state
    this.formData = {};
    this.isValid = false;
    
    // Elements
    this.prevBtn = formElement.querySelector('[data-action="prev"]');
    this.nextBtn = formElement.querySelector('[data-action="next"]');
    this.submitBtn = formElement.querySelector('[data-action="submit"]');
    this.progressBar = formElement.querySelector('.bxyz-form-progress__bar');
    this.progressFill = formElement.querySelector('[data-progress-fill]');
    this.progressCurrent = formElement.querySelector('.bxyz-form-progress__current');
    this.progressTotal = formElement.querySelector('.bxyz-form-progress__total');
    this.successMessage = formElement.querySelector('.bxyz-form-success');
    
    this.init();
  }
  
  init() {
    // Set total steps
    if (this.progressTotal) {
      this.progressTotal.textContent = this.totalSteps;
    }
    
    // Initialize GSAP ScrollTrigger for viewport animations
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      this.initScrollAnimations();
    }
    
    // Setup step mode or show all
    if (this.stepMode) {
      this.initStepMode();
    } else {
      this.showAllSteps();
    }
    
    // Event listeners
    this.attachEventListeners();
    
    // Auto-save to localStorage
    this.loadSavedData();
    this.enableAutoSave();
  }
  
  initScrollAnimations() {
    // Animate form container on scroll
    gsap.from(this.container, {
      opacity: 0,
      y: 50,
      duration: this.animationDuration * 1.5,
      ease: this.animationEasing,
      scrollTrigger: {
        trigger: this.container,
        start: 'top 80%',
        toggleActions: 'play none none none'
      }
    });
  }
  
  initStepMode() {
    // Hide all steps except first
    this.steps.forEach((step, index) => {
      if (index === 0) {
        step.style.display = 'block';
        step.style.opacity = '1';
        step.style.visibility = 'visible';
        step.classList.add('active');
        this.animateStepIn(step);
      } else {
        step.style.display = 'none';
        step.classList.remove('active');
      }
    });
    
    this.updateControls();
    this.updateProgress();
  }
  
  showAllSteps() {
    // Show all steps at once with stagger
    this.steps.forEach((step, index) => {
      step.classList.add('active');
      step.style.position = 'relative';
      step.style.visibility = 'visible';
      
      if (typeof gsap !== 'undefined') {
        gsap.from(step, {
          opacity: 0,
          x: -30,
          duration: this.animationDuration,
          delay: index * 0.1,
          ease: this.animationEasing
        });
      } else {
        step.style.opacity = '1';
      }
    });
    
    // Hide step controls
    if (this.prevBtn) this.prevBtn.style.display = 'none';
    if (this.nextBtn) this.nextBtn.style.display = 'none';
    if (this.submitBtn) this.submitBtn.style.display = 'block';
    if (this.progressBar) this.progressBar.parentElement.style.display = 'none';
  }
  
  attachEventListeners() {
    // Navigation buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.previousStep());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextStep());
    }
    
    // Form submission
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    
    // Input events - handle multiple inputs per step
    this.steps.forEach(step => {
      const inputs = step.querySelectorAll('.bxyz-form-input');
      inputs.forEach(input => {
        // Focus animations
        input.addEventListener('focus', (e) => this.handleInputFocus(e));
        input.addEventListener('blur', (e) => this.handleInputBlur(e));
        
        // Real-time validation
        input.addEventListener('input', (e) => this.handleInputChange(e));
        
        // Keyboard navigation
        input.addEventListener('keydown', (e) => this.handleKeydown(e));
      });
    });
  }
  
  handleInputFocus(e) {
    const input = e.target;
    const label = input.previousElementSibling;
    
    if (typeof gsap !== 'undefined') {
      gsap.to(input, {
        scale: 1.02,
        duration: 0.2,
        ease: 'power2.out'
      });
      
      if (label) {
        gsap.to(label, {
          color: this.container.dataset.accentColor || '#808080',
          duration: 0.3,
          ease: 'power2.out'
        });
      }
    }
  }
  
  handleInputBlur(e) {
    const input = e.target;
    const label = input.previousElementSibling;
    
    if (typeof gsap !== 'undefined') {
      gsap.to(input, {
        scale: 1,
        duration: 0.2,
        ease: 'power2.out'
      });
      
      if (label) {
        gsap.to(label, {
          color: this.container.dataset.textColor || '#FFFFFF',
          duration: 0.3,
          ease: 'power2.out'
        });
      }
    }
    
    this.validateField(input);
  }
  
  handleInputChange(e) {
    const input = e.target;
    const fieldName = input.dataset.field;
    this.formData[fieldName] = input.value;
  }
  
  handleKeydown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (this.stepMode) {
        this.nextStep();
      }
    }
  }
  
  validateField(input) {
    let isValid = true;
    let message = '';
    
    // Check if this specific input is required
    const isRequired = input.hasAttribute('required');
    
    if (isRequired && !input.value.trim()) {
      isValid = false;
      message = 'This field is required';
    } else if (input.type === 'email' && input.value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(input.value)) {
        isValid = false;
        message = 'Please enter a valid email';
      }
    }
    
    return isValid;
  }
  
  validateStep(stepEl) {
    const validation = stepEl.querySelector('.bxyz-form-validation');
    const inputs = stepEl.querySelectorAll('.bxyz-form-input');
    let allValid = true;
    let message = '';
    
    inputs.forEach(input => {
      if (!this.validateField(input)) {
        allValid = false;
        if (input.type === 'email') {
          message = 'Please enter a valid email';
        } else if (input.hasAttribute('required')) {
          message = 'This field is required';
        }
      }
    });
    
    if (validation) {
      validation.textContent = message;
      
      if (typeof gsap !== 'undefined' && message) {
        gsap.from(validation, {
          opacity: 0,
          y: -10,
          duration: 0.3,
          ease: 'power2.out'
        });
      }
    }
    
    return allValid;
  }
  
  nextStep() {
    if (this.currentStep >= this.totalSteps - 1) return;
    
    // Validate current step (all inputs in step)
    const currentStepEl = this.steps[this.currentStep];
    
    if (!this.validateStep(currentStepEl)) {
      const firstInput = currentStepEl.querySelector('.bxyz-form-input');
      if (firstInput) this.shakeInput(firstInput);
      return;
    }
    
    // Animate out current step
    this.animateStepOut(currentStepEl, () => {
      currentStepEl.classList.remove('active');
      currentStepEl.style.display = 'none';
      
      // Move to next step
      this.currentStep++;
      const nextStepEl = this.steps[this.currentStep];
      nextStepEl.style.display = 'block';
      
      // Animate in next step
      setTimeout(() => {
        nextStepEl.classList.add('active');
        this.animateStepIn(nextStepEl);
        
        // Focus input
        const nextInput = nextStepEl.querySelector('.bxyz-form-input');
        if (nextInput) {
          setTimeout(() => nextInput.focus(), this.animationDuration * 500);
        }
      }, 50);
      
      this.updateControls();
      this.updateProgress();
      
      // Scroll to form if needed
      if (window.lenis) {
        window.lenis.scrollTo(this.container, {
          offset: -100,
          duration: 1
        });
      }
    });
  }
  
  previousStep() {
    if (this.currentStep <= 0) return;
    
    const currentStepEl = this.steps[this.currentStep];
    
    this.animateStepOut(currentStepEl, () => {
      currentStepEl.classList.remove('active');
      currentStepEl.style.display = 'none';
      
      this.currentStep--;
      const prevStepEl = this.steps[this.currentStep];
      prevStepEl.style.display = 'block';
      
      setTimeout(() => {
        prevStepEl.classList.add('active');
        this.animateStepIn(prevStepEl);
        
        const prevInput = prevStepEl.querySelector('.bxyz-form-input');
        if (prevInput) {
          setTimeout(() => prevInput.focus(), this.animationDuration * 500);
        }
      }, 50);
      
      this.updateControls();
      this.updateProgress();
    });
  }
  
  animateStepIn(step) {
    // Ensure step is visible
    step.style.display = 'block';
    step.style.visibility = 'visible';
    
    if (typeof gsap === 'undefined') {
      step.style.opacity = '1';
      return;
    }
    
    const label = step.querySelector('.bxyz-form-label');
    const input = step.querySelector('.bxyz-form-input');
    
    gsap.fromTo(step, 
      { opacity: 0, x: 50 },
      { 
        opacity: 1, 
        x: 0, 
        duration: this.animationDuration,
        ease: this.animationEasing
      }
    );
    
    if (label) {
      gsap.from(label, {
        opacity: 0,
        y: -20,
        duration: this.animationDuration * 0.8,
        delay: this.animationDuration * 0.2,
        ease: this.animationEasing
      });
    }
    
    if (input) {
      gsap.from(input, {
        opacity: 0,
        scaleX: 0.8,
        duration: this.animationDuration * 0.8,
        delay: this.animationDuration * 0.3,
        ease: this.animationEasing,
        transformOrigin: 'left center'
      });
    }
  }
  
  animateStepOut(step, callback) {
    if (typeof gsap === 'undefined') {
      step.style.opacity = '0';
      if (callback) callback();
      return;
    }
    
    gsap.to(step, {
      opacity: 0,
      x: -50,
      duration: this.animationDuration * 0.7,
      ease: 'power2.in',
      onComplete: callback
    });
  }
  
  shakeInput(input) {
    if (typeof gsap === 'undefined') return;
    
    gsap.to(input, {
      x: [-10, 10, -10, 10, 0],
      duration: 0.4,
      ease: 'power2.out'
    });
  }
  
  updateControls() {
    if (!this.stepMode) return;
    
    // Previous button
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentStep === 0;
      this.prevBtn.style.display = this.currentStep === 0 ? 'none' : 'block';
    }
    
    // Next/Submit button
    const isLastStep = this.currentStep === this.totalSteps - 1;
    
    if (this.nextBtn) {
      this.nextBtn.style.display = isLastStep ? 'none' : 'block';
    }
    
    if (this.submitBtn) {
      this.submitBtn.style.display = isLastStep ? 'block' : 'none';
    }
  }
  
  updateProgress() {
    const progress = ((this.currentStep + 1) / this.totalSteps) * 100;
    
    if (this.progressFill) {
      if (typeof gsap !== 'undefined') {
        gsap.to(this.progressFill, {
          width: `${progress}%`,
          duration: 0.5,
          ease: 'power2.out'
        });
      } else {
        this.progressFill.style.width = `${progress}%`;
      }
    }
    
    if (this.progressCurrent) {
      this.progressCurrent.textContent = this.currentStep + 1;
    }
  }
  
  handleSubmit(e) {
    e.preventDefault();
    
    // Validate all steps
    let allValid = true;
    this.steps.forEach(step => {
      if (!this.validateStep(step)) {
        allValid = false;
      }
    });
    
    if (!allValid) {
      this.shakeInput(this.form);
      return;
    }
    
    // Actually submit the form
    const formData = new FormData(this.form);
    
    fetch(this.form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json'
      }
    }).then(response => {
      // Animate form out and show success
      if (typeof gsap !== 'undefined') {
        gsap.to(this.form, {
          opacity: 0,
          y: -30,
          duration: this.animationDuration,
          ease: 'power2.in',
          onComplete: () => {
            this.form.style.display = 'none';
            this.showSuccess();
          }
        });
      } else {
        this.form.style.display = 'none';
        this.showSuccess();
      }
      
      // Clear saved data
      this.clearSavedData();
    }).catch(error => {
      console.error('Form submission error:', error);
      // Fallback: submit form normally
      this.form.submit();
    });
  }
  
  showSuccess() {
    if (!this.successMessage) return;
    
    this.successMessage.style.display = 'block';
    
    if (typeof gsap !== 'undefined') {
      gsap.from(this.successMessage, {
        opacity: 0,
        scale: 0.8,
        duration: this.animationDuration * 1.5,
        ease: 'back.out(1.7)'
      });
    } else {
      this.successMessage.style.opacity = '1';
    }
  }
  
  // Auto-save functionality
  enableAutoSave() {
    this.form.addEventListener('input', () => {
      localStorage.setItem('bxyz-form-data', JSON.stringify(this.formData));
    });
  }
  
  loadSavedData() {
    const saved = localStorage.getItem('bxyz-form-data');
    if (saved) {
      try {
        this.formData = JSON.parse(saved);
        Object.keys(this.formData).forEach(key => {
          const input = this.form.querySelector(`[data-field="${key}"]`);
          if (input) {
            input.value = this.formData[key];
          }
        });
      } catch (e) {
        console.error('Failed to load saved form data', e);
      }
    }
  }
  
  clearSavedData() {
    localStorage.removeItem('bxyz-form-data');
    this.formData = {};
  }
}

// Initialize all forms
document.addEventListener('DOMContentLoaded', () => {
  const forms = document.querySelectorAll('[data-bxyz-form]');
  forms.forEach(form => {
    new BXYZInteractiveForm(form);
  });
});
